import React from 'react';
import { Mail, Phone, MapPin, Heart, Facebook, Twitter, Instagram, ExternalLink } from 'lucide-react';
import { siteData } from '../data/mock';

const socialIconMap = {
  Facebook: Facebook,
  Twitter: Twitter,
  Instagram: Instagram
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer id="contact" className="bg-stone-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center">
                <span className="text-xl font-bold text-white">JA</span>
              </div>
              <div>
                <p className="font-bold text-xl">{siteData.siteName}</p>
                <p className="text-stone-400 text-sm">{siteData.tagline}</p>
              </div>
            </div>
            <p className="text-stone-400 leading-relaxed mb-6 max-w-md">
              A humanitarian initiative dedicated to supporting Jamaican workers 
              and families through advocacy, education, and direct assistance.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-4">
              {siteData.socialLinks.map((social) => {
                const IconComponent = socialIconMap[social.icon];
                return (
                  <a
                    key={social.platform}
                    href={social.url}
                    className="w-10 h-10 rounded-lg bg-stone-800 hover:bg-emerald-600 flex items-center justify-center transition-colors"
                    aria-label={social.platform}
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-4">
              <li>
                <a href="#why-it-matters" className="text-stone-400 hover:text-white transition-colors flex items-center gap-2">
                  Why It Matters
                </a>
              </li>
              <li>
                <a href="#mission" className="text-stone-400 hover:text-white transition-colors flex items-center gap-2">
                  Our Mission
                </a>
              </li>
              <li>
                <a href="#help" className="text-stone-400 hover:text-white transition-colors flex items-center gap-2">
                  How To Help
                </a>
              </li>
              <li>
                <a 
                  href={siteData.donateUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-2 font-semibold"
                >
                  <Heart className="w-4 h-4" />
                  Donate Now
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`mailto:${siteData.contact.email}`}
                  className="text-stone-400 hover:text-white transition-colors flex items-center gap-3"
                >
                  <Mail className="w-5 h-5 text-emerald-500" />
                  {siteData.contact.email}
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${siteData.contact.phone}`}
                  className="text-stone-400 hover:text-white transition-colors flex items-center gap-3"
                >
                  <Phone className="w-5 h-5 text-emerald-500" />
                  {siteData.contact.phone}
                </a>
              </li>
              <li className="flex items-center gap-3 text-stone-400">
                <MapPin className="w-5 h-5 text-emerald-500" />
                {siteData.contact.address}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-stone-500 text-sm">
              © {currentYear} {siteData.siteName}. All rights reserved.
            </p>
            <p className="text-stone-500 text-sm flex items-center gap-2">
              Made with <Heart className="w-4 h-4 text-red-500" /> for Jamaica
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
